<template>
  <VSection v-if="hasBundlePurchase" id="bundle_purchase" label="transactions.bundle_purchase_headline" level="5">
    <transition name="fade" mode="out-in" appear>
      <VMessage v-if="loadOnDemand.msgBox.type" v-bind="loadOnDemand.msgBox"></VMessage>
      <div v-else-if="!loadOnDemand.isLoading" class="row">
        <div v-if="loadOnDemand.errorMessage.length > 0" class="col-l-12 col-m-12">
          <br />
          <div class="notification notification-negative" role="alert">
            <h3 class="notification-heading">{{ $t('common.headerError') }}</h3>
            <div class="notification-content">
              <br />
              <strong> {{ $t('transactions.msgErrOnReloadHeader') }} </strong>
              <br />
              {{ loadOnDemand.errorMessage.toString() }}
            </div>
          </div>
        </div>

        <div v-else-if="loadOnDemandStorage.bundlePurchaseData">
          <div class="col-l-5 col-m-10">
            <VSection group level="6" label="transactions.bundle_purchase_template_headline">
              <VDescriptionList>
                <VEntry :data="transactionDetails" field="bundleTemplateIdentifier" name="transactions.bundle_purchase_template_id_label" />
                <VEntryDate :data="transactionDetails" field="bundleTemplateValidFrom" name="transactions.bundle_purchase_bookable_from_label" />
                <VEntryDate :data="transactionDetails" field="bundleTemplateValidTo" name="transactions.bundle_purchase_bookable_to_label" />
              </VDescriptionList>
            </VSection>
          </div>

          <div class="col-l-7 col-m-14">
            <VSection level="6" group label="transactions.bundle_purchase_bundle_instances_headline">
              <VueGoodTable
                style-class="table table-hover"
                :columns="tableColumnsBundleInstances"
                :rows="getDetailsBundlePurchaseInstancesRows"
                :paginate="true"
                :rows-per-page-text="$t('common.tablePerPage')"
                :of-text="$t('common.tableOf')"
                :all-text="$t('common.tableAll')"
                :prev-text="$t('common.tablePrev')"
                :next-text="$t('common.tableNext')"
                :row-style-class="''"
                :line-numbers="false"
              >
                <template #table-column="{ column }">
                  <VTableHeader :columns="tableColumnsBundleInstances" :column="column" locale-namespace="transactions" />
                </template>
              </VueGoodTable>
            </VSection>
          </div>
        </div>
        <!-- end show bundle purchase data -->

        <!-- Load Button for bundle purchase !loadOnDemand.hasBundlePurchaseCompleted  -->
        <div
          v-if="
            !loadOnDemand.msgBox.textKey &&
              !loadOnDemandStorage.bundlePurchaseData &&
              (!loadOnDemand.errorMessage.length > 0 || loadOnDemand.errorCodeType === 'RETRY')
          "
          class="col-l-5 col-m-10"
        >
          <VButton class="btn btn-default" label="transactions.btnShowBundlePurchase" @click="loadOnDemandBundlePurchase" />
        </div>
      </div>
    </transition>
  </VSection>
  <!-- end lazy load bundle purchase -->
</template>

<script>
import { formatDateTime } from '@/utils/date';
import Proxy from '@/proxies/PaymentTransactionsProxy';
import VSection from '@/components/Section';
import VDescriptionList from '@/components/DescriptionList';
import VEntry from '@/components/DescriptionList/Entry';
import VEntryDate from '@/components/DescriptionList/EntryDate';
import VButton from '@/components/Button';
import MessageMixin from '@/mixins/message';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';

const tableColumnsBundleInstances = [
  {
    label: 'transactions.bundle_purchase_bundle_instance_id_column_heading',
    field: 'id',
    sortable: true,
    filterable: false,
  },
  {
    label: 'transactions.bundle_purchase_created_column_heading',
    field: 'creationDate',
    sortable: true,
    filterable: false,
    type: 'string',
  },
  {
    label: 'transactions.bundle_purchase_usable_from_column_heading',
    field: 'usableFrom',
    sortable: true,
    filterable: false,
    type: 'string',
  },
  {
    label: 'transactions.bundle_purchase_usable_to_column_heading',
    field: 'usableTo',
    sortable: true,
    filterable: false,
    type: 'string',
  },
];

export default {
  name: 'PaymentTransactionDetailsProductBundlePurchase',

  components: {
    VueGoodTable,
    VSection,
    VDescriptionList,
    VEntry,
    VEntryDate,
    VButton,
    VMessage: () => import('@/components/Message'),
    VTableHeader,
  },

  mixins: [MessageMixin],

  props: {
    // input data
    transactionDetails: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      tableColumnsBundleInstances,
      loadOnDemand: {
        isLoading: false,
        foundNoData: false,
        errorMessage: '',
        errorCode: '',
        errorCodeType: '',
        msgBox: {
          type: '',
          textKey: '',
        },
      },
      loadOnDemandStorage: {
        bundlePurchaseData: null,
      },
      currentTransactionId: null,
    };
  },

  computed: {
    hasBundlePurchase() {
      if (!this.transactionDetails) {
        return false;
      }
      return !!this.transactionDetails.bundleTemplateIdentifier;
    },

    getDetailsBundlePurchaseInstancesRows() {
      if (!this.transactionDetails) {
        return null;
      }
      if (this.loadOnDemandStorage === null || this.loadOnDemandStorage.bundlePurchaseData === null) {
        return null;
      }

      return this.translateRows(this.loadOnDemandStorage.bundlePurchaseData);
    },
  },

  methods: {
    async loadOnDemandBundlePurchase() {
      this.clearCompMessage(this.loadOnDemand.msgBox);

      this.loadOnDemand.isLoading = true;
      this.loadOnDemand.errorMessage = '';
      this.loadOnDemand.errorCodeType = '';
      this.loadOnDemand.errorCode = '';
      this.loadOnDemand.foundNoData = false;

      this.setCompMessage(this.loadOnDemand.msgBox, 'LOADING');
      try {
        const data = await new Proxy({
          partitionIndex: this.transactionDetails.partitionIndex,
        }).bundlePurchase(this.transactionDetails.transactionId);
        if (data === null) {
          this.loadOnDemandStorage.bundlePurchaseData = null;
          this.loadOnDemand.foundNoData = true;
          this.setCompMessage(this.loadOnDemand.msgBox, 'ERROR', 'transactions.msgErrFoundNoData');
        } else {
          this.loadOnDemandStorage.bundlePurchaseData = data;
          this.clearCompMessage(this.loadOnDemand.msgBox);

          this.currentTransactionId = this.transactionDetails.transactionId;
        }
      } catch (error) {
        this.loadOnDemandStorage.bundlePurchaseData = null;

        this.loadOnDemand.errorCodeType = error?.data?.codeType;
        this.loadOnDemand.errorCode = error?.data?.code;
        this.loadOnDemand.errorMessage = this.loadOnDemand.errorCode ? this.loadOnDemand.errorCode : 'UNKNOWN';

        this.clearCompMessage(this.loadOnDemand.msgBox);
      }
      this.loadOnDemand.isLoading = false;
    },

    translateRows(rows) {
      if (rows) {
        rows.map(row => {
          row.creationDate = formatDateTime(row.creationDate);
          row.usableFrom = formatDateTime(row.usableFrom);
          row.usableTo = formatDateTime(row.usableTo);
          return row;
        });
      }
      return rows;
    },
  },
};
</script>
